import { z } from 'zod'

/**
 * Stabiele identiteit voor alles wat in een definitie of een instantie een naam
 * moet hebben (ontwerp 4.3). Drie regels, en ze zijn hard:
 *
 * 1. Een id wordt bij het aanmaken toegekend en wijzigt nooit.
 * 2. Een id wordt nooit hergebruikt, ook niet nadat het ding is verwijderd.
 * 3. Een id draagt geen betekenis. Label wijzigen, vertalen, verplaatsen tussen
 *    secties of kolommen: de identiteit blijft.
 *
 * De PoC deed dit met `editPath ?? type + '-' + label`, een positioneel indexpad
 * dat verschuift zodra iets wordt gesleept. Voor antwoordopslag, revisies en
 * wijzigingsdetectie per veld (R-97) is dat dodelijk; daarom staat dit bestand
 * vooraan in M0 en hangt de rest eraan.
 *
 * Uit regel 3 volgt iets wat bij het lezen van ontwerp 4.4 tegenintuïtief is:
 * de voorvoegsels `grp-` en `cmp-` in dat voorbeeld zeggen niets bindend over
 * het controltype. Een voorvoegsel is er om een id in een log of een foutmelding
 * te kunnen plaatsen, niet om er een type uit af te leiden. Een veld dat van
 * controltype verandert houdt zijn id, dus `VeldId` accepteert alle drie de
 * voorvoegsels zonder ze aan een `control` te binden.
 */

/**
 * 33 tekens: de cijfers plus de kleine letters zonder i, l en o. Die drie zijn
 * met 1 en 0 te verwarren, en een id wordt gelezen, voorgelezen en overgetypt
 * in foutmeldingen. Andersom hoeft niet: 0 en 1 blijven erin, want wie geen l
 * of o kan tegenkomen, kan zich er ook niet in vergissen.
 */
const ALPHABET = '0123456789abcdefghjkmnpqrstuvwxyz'

/** Hetzelfde alfabet als tekenklasse: a-h, j-k, m, n, p-z laat i, l en o weg. */
const ALPHABET_CLASS = '[0-9a-hj-kmnp-z]'

/**
 * Tien tekens uit 33 is ongeveer 1,5 * 10^15 mogelijkheden. Dat is niet de
 * uniciteitsgarantie: die komt van de controle op dubbele ids per definitie
 * (opdracht 3). Dit is de lengte waarbij een botsing binnen één definitie geen
 * reëel scenario meer is en een id nog te lezen blijft.
 */
const LENGTH = 10

/**
 * Bytes vanaf deze waarde worden weggegooid. 256 is geen veelvoud van 33, dus
 * zonder deze afkap zouden de eerste 25 tekens van het alfabet iets vaker
 * voorkomen dan de rest.
 */
const TRUNCATION = 256 - (256 % ALPHABET.length)

/** Waar een id vandaan komt. Alleen het voorvoegsel verschilt. */
export const PREFIX = {
  definitie: 'def',
  sectie: 'sec',
  veld: 'vld',
  herhaalgroep: 'grp',
  component: 'cmp',
  regel: 'rgl',
  rij: 'rij',
  publicatie: 'pub',
  instantie: 'ins',
  revisie: 'rev',
  deling: 'dln',
  event: 'evt',
  draad: 'drd',
  bericht: 'ber',
  standaardveld: 'std',
  // Niet `bro`: dat zou botsen met de bronnenwortel `bronnen` in het sectiedocument
  // (`packages/renderer/src/document/sectiedocument.ts`, `SOURCES`).
  bron: 'brn',
  citatie: 'cit',
  toekenning: 'tkn',
  uitnodiging: 'utn',
  advies: 'adv',
  reactie: 'rct',
  // Een automatisch versiemoment van een sectiedocument (ontwerp 6.9, `versiemoment.ts`).
  versiemoment: 'vsm',
  // Een regel in het stempelregister van de opmerkingen (eisenboek 12.46, `stempel.ts`).
  stempel: 'stp',
  // Een sjabloon in de bibliotheek van formulierconfiguraties (D-34, besluit van 1 november 2026).
  sjabloon: 'sjb',
  // Een uitrolverzoek: een definitie die de poort en het akkoord haalde en op de uitrolpijplijn wacht
  // (ontwerp 8.8, besluit van 2 november 2026, migratie 0054).
  uitrolverzoek: 'uvz',
  // Een publicatieaanbod: een versie die iemand met Wijzigen ter publicatie aanbiedt en die een tweede
  // persoon met Publicatieakkoord goedkeurt (vier ogen, besluit van 3 november 2026, migratie 0056).
  publicatieaanbod: 'pab',
} as const

export type Kind = keyof typeof PREFIX

/**
 * Bouwt het schema voor een id met een of meer toegestane voorvoegsels.
 *
 * De staart is 1 tot 16 tekens en niet exact tien. Dat is geen slordigheid: de
 * voorbeelden in ontwerp 4.4 en 4.5 dragen korte ids (`sec-p2m`, `rgl-1`,
 * `rev-7u2`) en die moeten valideren, want het schema is de toets op het
 * document en niet omgekeerd. De generator maakt er tien; met de hand
 * geschreven materiaal mag korter.
 */
function idSchema(...prefixes: readonly string[]) {
  const choice = prefixes.length === 1 ? prefixes[0] : `(?:${prefixes.join('|')})`
  const pattern = new RegExp(`^${choice}-${ALPHABET_CLASS}{1,16}$`)
  return z.string().regex(pattern, `verwacht een id van de vorm ${prefixes.join(' of ')}-xxxxxxxxxx`)
}

export const DefinitieId = idSchema(PREFIX.definitie)
export const SectionId = idSchema(PREFIX.sectie)
export const RuleId = idSchema(PREFIX.regel)
export const RowId = idSchema(PREFIX.rij)
export const PublicatieId = idSchema(PREFIX.publicatie)
export const InstantieId = idSchema(PREFIX.instantie)
export const RevisieId = idSchema(PREFIX.revisie)
export const DelingId = idSchema(PREFIX.deling)
export const EventId = idSchema(PREFIX.event)
/** Een toekenning in het register (R-155b, ontwerp 4.8). */
export const ToekenningId = idSchema(PREFIX.toekenning)
/** Een uitnodiging op een eenmalige code (R-64c, ontwerp 4.8). */
export const InvitationId = idSchema(PREFIX.uitnodiging)
/** Een adviesverzoek (R-184, besluit van 30 oktober 2026). */
export const AdviesId = idSchema(PREFIX.advies)
/** Eén reactie in het gesprek van een adviesverzoek. */
export const ReactieId = idSchema(PREFIX.reactie)
export const VersiemomentId = idSchema(PREFIX.versiemoment)
export const StempelId = idSchema(PREFIX.stempel)

/**
 * Een opmerkingdraadje en een bericht daarin (R-73).
 *
 * Gegenereerd en niet door een mens gekozen, in tegenstelling tot het criterium-id: een
 * draadje is geen domeinbegrip waar buiten dit product iets aan hangt. Het draad-id staat
 * ook in de tekst zelf, als de mark die het anker vasthoudt (ontwerp 6.8), en die verhuist
 * mee als de tekst verschuift.
 */
export const ThreadId = idSchema(PREFIX.draad)
export const DefaultFieldId = idSchema(PREFIX.standaardveld)
export type DefaultFieldId = z.infer<typeof DefaultFieldId>
/** Een sjabloon: een bewaarde opzet van een formulier, met versies (D-34, besluit van 1 november 2026). */
export const TemplateId = idSchema(PREFIX.sjabloon)
export type TemplateId = z.infer<typeof TemplateId>
/** Een uitrolverzoek (ontwerp 8.8, migratie 0054). */
export const UitrolverzoekId = idSchema(PREFIX.uitrolverzoek)
export type UitrolverzoekId = z.infer<typeof UitrolverzoekId>
/** Een publicatieaanbod (vier ogen op publiceren, besluit van 3 november 2026, migratie 0056). */
export const PublicatieaanbodId = idSchema(PREFIX.publicatieaanbod)
export type PublicatieaanbodId = z.infer<typeof PublicatieaanbodId>

/**
 * De vaste sleutel van een standaardveld, zoals `titel-voorstel` (besluit van 1 november 2026).
 *
 * Naast het id en niet in plaats ervan. Het id is gegenereerd en zegt niets; de sleutel is leesbaar
 * en zegt "dit is dezelfde vraag", ook in een formulier van een andere ronde waar het geplaatste veld
 * een eigen veld-id heeft. Hij verandert nooit, ook niet bij een nieuwe versie, want dan zou hij twee
 * vragen dezelfde naam geven. Kleine letters, cijfers en enkele koppeltekens, zodat hij in een pad,
 * een XML-attribuut en een rapportage zonder omzetting past.
 */
export const DefaultFieldKey = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'een sleutel van kleine letters en cijfers, met enkele koppeltekens ertussen, zoals titel-voorstel')
  .max(64)
export type DefaultFieldKey = z.infer<typeof DefaultFieldKey>
export const MessageId = idSchema(PREFIX.bericht)

/**
 * Een bron in de bronnenopslag van een sectiedocument (R-50, ontwerp 6.8).
 *
 * Net als het draad-id een gegenereerd id en geen domeinbegrip: welke bron een invuller
 * aan een citatie hangt is aan hem, en de titel of DOI is de leesbare identiteit, niet dit id.
 */
export const SourceId = idSchema(PREFIX.bron)

/** Een veld, een herhaalgroep of een geplaatst component: alle drie zijn velden. */
export const FieldId = idSchema(PREFIX.veld, PREFIX.herhaalgroep, PREFIX.component)

/**
 * Het criterium-id is geen gegenereerd id maar domein-identiteit (R-150): eraan
 * hangen weging, ranking en aggregatie, en die leven buiten dit product
 * (R-151). Het wordt dus door een mens gekozen en moet leesbaar zijn, zoals
 * `crit-volledig-oc26` in ontwerp 4.4. Vandaar een slug en geen willekeur.
 */
const SLUG = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/

export const CriterionId = z
  .string()
  .regex(SLUG, 'een criterium-id is een slug van kleine letters, cijfers en koppeltekens')

/**
 * De identiteit van een compartiment in het contract van een bouwblok (D-69, D-70).
 *
 * Zoals `te-beoordelen` en `niet-voor-beoordeling` in ontwerp 9.5. Net als het
 * criterium-id door een mens gekozen en van buiten het formulier: welke compartimenten een
 * bouwblok heeft staat in de bouwblokdefinitie en niet in het formulier (D-70). Het
 * label komt daarbij uit het contract; het model kent alleen de identiteit.
 *
 * Eigen naam en dezelfde vorm als `CriteriumId`, want het zijn twee begrippen en niet
 * één: een criterium hangt aan een toets en een compartiment aan een contract.
 */
export const CompartimentId = z
  .string()
  .regex(SLUG, 'een compartiment-id is een slug van kleine letters, cijfers en koppeltekens')

/**
 * De aanduiding van een bouwblok, zoals `aanvraag-indienen` of `toets-aanvraag`.
 *
 * Net als het criterium-id geen gegenereerd id maar iets wat van buiten komt: de
 * bouwblokken van een ronde staan vast voordat de formulieren worden gemaakt (D-41),
 * en de configurator krijgt ze aangeleverd als beschreven contracten (U26). Dezelfde
 * slug staat in het `$id` van een contract (`grantify:contract:<blok>:<ronde>`), in de
 * registry met de ketenvolgorde, en als sleutel in `DossierDocuments`. Eén schema
 * voor alle vier, want vier keer dezelfde regex is vier keer een kans om af te wijken.
 */
export const Block = z.string().regex(/^[a-z0-9-]+$/, 'een blokaanduiding is een slug')

/**
 * Het patroon van een rondesleutel, zoals `2026-1`: een jaartal en een volgnummer.
 *
 * Eén keer hier, want hij staat op twee plekken: als sleutel van een ronde in de opslag
 * (`RondeSleutel`) en als laatste deel van het `$id` van een contract
 * (`grantify:contract:<blok>:<ronde>`, ontwerp 9.5). De sleutel is dezelfde die het processysteem
 * gebruikt (D-75, besluit van 1 november 2026), zodat een ronde die later van het processysteem
 * komt op die sleutel wordt gekoppeld.
 */
export const RONDE_PATROON = '[0-9]{4}-[0-9]+'

/** De sleutel van een ronde (D-75). */
export const RondeSleutel = z.string().regex(new RegExp(`^${RONDE_PATROON}$`), 'een rondesleutel is een jaartal en een volgnummer, zoals 2026-1')

/**
 * De sleutel van een domein, zoals `enw` of `nwa-kic` (D-75). Een slug, want hij staat in een
 * query en in een rij; de leesbare afkorting en naam staan ernaast.
 */
export const DomeinSleutel = z.string().regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/, 'een domeinsleutel is een slug').max(32)

/**
 * Het uitvraagmoment: de openstelling waarvoor dit formulier is gepubliceerd en
 * waarop een dossier is uitgezet.
 *
 * Voor dit product een ondoorzichtige verwijzing naar iets bij de gastheer. Hij staat
 * op de instantie (R-100) en op de publicatie, want per bouwblok en per uitvraagmoment
 * geldt maar één gepubliceerd formulier (D-67) en een volgend moment is een nieuw
 * formulier en geen nieuwe versie (R-102, D-08).
 */
export const UitvraagRef = z.string().min(1)

/**
 * Het dossier bij het processysteem. Voor dit product een ondoorzichtige verwijzing.
 *
 * Een instantie hangt eraan (U21), en een toekenning geldt ervoor (R-155c). Eén schema voor
 * beide, want het is één begrip: een toekenning van het processysteem geldt voor elke
 * instantie van haar bouwblokken op dat dossier, en die vergelijking moet over dezelfde
 * waarde gaan.
 */
export const DossierId = z.string().min(1)

export type Block = z.infer<typeof Block>
export type UitvraagRef = z.infer<typeof UitvraagRef>
export type RondeSleutel = z.infer<typeof RondeSleutel>
export type DomeinSleutel = z.infer<typeof DomeinSleutel>
export type DossierId = z.infer<typeof DossierId>
export type ToekenningId = z.infer<typeof ToekenningId>
export type CompartimentId = z.infer<typeof CompartimentId>
export type DefinitieId = z.infer<typeof DefinitieId>
export type SectionId = z.infer<typeof SectionId>
export type FieldId = z.infer<typeof FieldId>
export type RuleId = z.infer<typeof RuleId>
export type RowId = z.infer<typeof RowId>
export type PublicatieId = z.infer<typeof PublicatieId>
export type InstantieId = z.infer<typeof InstantieId>
export type RevisieId = z.infer<typeof RevisieId>
export type DelingId = z.infer<typeof DelingId>
export type EventId = z.infer<typeof EventId>
export type ThreadId = z.infer<typeof ThreadId>
export type MessageId = z.infer<typeof MessageId>
export type SourceId = z.infer<typeof SourceId>
export type CriterionId = z.infer<typeof CriterionId>
export type AdviesId = z.infer<typeof AdviesId>
export type ReactieId = z.infer<typeof ReactieId>
export type VersiemomentId = z.infer<typeof VersiemomentId>

/**
 * Een willekeurige staart van `length` tekens uit het alfabet.
 *
 * Gebruikt `globalThis.crypto`, dat in Node 22 en in elke doelbrowser bestaat:
 * dit pakket moet isomorf blijven, dus geen `node:crypto`.
 */
function tail(length: number): string {
  let out = ''
  const buffer = new Uint8Array(length * 2)
  while (out.length < length) {
    globalThis.crypto.getRandomValues(buffer)
    for (const byte of buffer) {
      if (byte >= TRUNCATION) continue
      out += ALPHABET.charAt(byte % ALPHABET.length)
      if (out.length === length) break
    }
  }
  return out
}

/**
 * Maakt een nieuw id. Dit is de enige plek waar een id ontstaat, ook voor
 * testdata en voor de twee voorbeelddefinities: met de hand verzonnen
 * identifiers voldoen zelden aan de eigen regels (ontwerp 11.4, bijlage B
 * probleem 15).
 */
export function newId(kind: Kind): string {
  return `${PREFIX[kind]}-${tail(LENGTH)}`
}
